import type { SfxKey } from '@/audio/AudioManager';
import { createLazyAudio } from '@/audio/lazy';
import { AdblockModal } from '@/components/AdblockModal';
import { GameOverScreen } from '@/components/GameOverScreen';
import type { GameStageControls } from '@/components/GameStage';
import { HowToPlayModal } from '@/components/HowToPlayModal';
import { LeaderboardModal } from '@/components/LeaderboardModal';
import { PauseOverlay } from '@/components/PauseOverlay';
import { PrivacyModal } from '@/components/PrivacyModal';
import { SettingsModal } from '@/components/SettingsModal';
import { TermsModal } from '@/components/TermsModal';
import { TitleScreen } from '@/components/TitleScreen';
import { UpgradeShopModal } from '@/components/UpgradeShopModal';
import { createBonusService } from '@/economy/bonus';
import { createCurrencyService } from '@/economy/currency';
import type { GoldEarnedResult } from '@/economy/types';
import type { GameOverInfo } from '@/game/GameScene';
import { useReducedMotion } from '@/hooks/useReducedMotion';
import type { InputModality } from '@/input/InputManager';
import { isLeaderboardConfigured } from '@/leaderboard/config';
import type { ScoreSubmission, SubmitOutcome } from '@/leaderboard/types';
import {
  AdGate,
  attachGlobalErrorCapture,
  createPlatformAdapter,
  resolvePlatform,
  resolvePokiMode,
} from '@/sdk';
import type { PokiAdapter } from '@/sdk';
import { ADS_ENABLED } from '@/sdk/features';
import { type PlayerSave, createPlayerStore } from '@/state/playerStore';
import {
  Suspense,
  lazy,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  useSyncExternalStore,
} from 'react';

const GameStage = lazy(() =>
  import('@/components/GameStage').then((m) => ({ default: m.GameStage })),
);

type Screen = 'title' | 'playing' | 'gameOver';
type ModalKey = 'settings' | 'howto' | 'leaderboard' | 'shop' | 'privacy' | 'terms' | null;

// BASE_URL = '/' for Poki (root-served), './' for CrazyGames (subpath-served).
// Vite replaces import.meta.env.BASE_URL at build time.
const BASE = import.meta.env.BASE_URL;
const SPRITE_URL = `${BASE}audio/sfx-sprite.json`;
// m4a first so Safari/iPad/iPhone get a format they can decode; Howler tries each in order.
const BG_MUSIC_URLS = [`${BASE}audio/bg-music.m4a`, `${BASE}audio/bg-music.ogg`];

export function parseChallengeScore(search: string): number | null {
  if (!search) return null;
  const params = new URLSearchParams(search);
  const raw = params.get('score');
  if (raw === null) return null;
  const n = Number(raw);
  if (!Number.isFinite(n) || n <= 0) return null;
  return Math.floor(n);
}

export function App() {
  const platform = resolvePlatform();
  const mode = resolvePokiMode();
  const adapter = useMemo<PokiAdapter>(
    () => createPlatformAdapter(platform, mode),
    [platform, mode],
  );
  const adGate = useMemo(() => new AdGate({ adapter, adsEnabled: ADS_ENABLED }), [adapter]);
  const save = useMemo(
    () =>
      createPlayerStore({
        captureError: (error, context) => adapter.captureError(error, context),
      }),
    [adapter],
  );
  const currency = useMemo(() => createCurrencyService(save), [save]);
  const bonus = useMemo(() => createBonusService(save), [save]);
  const saveState = useSyncExternalStore<PlayerSave>(
    (cb) => save.subscribe(cb),
    () => save.get(),
    () => save.get(),
  );
  const audio = useMemo(
    () =>
      createLazyAudio({
        spriteUrl: SPRITE_URL,
        bgMusicUrls: BG_MUSIC_URLS,
        isMuted: () => save.get().settings.muted,
        isVibrationEnabled: () => save.get().settings.vibrationEnabled,
        getMasterVolume: () => save.get().settings.masterVolume,
      }),
    [save],
  );
  const reducedMotion = useReducedMotion();
  const [ready, setReady] = useState(false);
  const [screen, setScreen] = useState<Screen>('title');
  const [openModal, setOpenModal] = useState<ModalKey>(null);
  const [gameOverInfo, setGameOverInfo] = useState<GameOverInfo | null>(null);
  const [lastGoldEarned, setLastGoldEarned] = useState<GoldEarnedResult | null>(null);
  const [lastWasPB, setLastWasPB] = useState(false);
  const [bonusActiveOnRetry, setBonusActiveOnRetry] = useState(false);
  const [attemptId, setAttemptId] = useState(0);
  const [paused, setPaused] = useState(false);
  const [adblockModalOpen, setAdblockModalOpen] = useState(false);
  const [challengeScore] = useState<number | null>(() =>
    typeof window === 'undefined' ? null : parseChallengeScore(window.location.search),
  );
  const stageControlsRef = useRef<GameStageControls | null>(null);

  useEffect(() => {
    const detach = attachGlobalErrorCapture((error, context) =>
      adapter.captureError(error, context),
    );
    let cancelled = false;
    adapter.gameLoadingStart();
    const finishLoading = () => {
      if (cancelled) return;
      adapter.gameLoadingFinished();
      adGate.notifyGameLoadingFinished();
      setReady(true);
    };
    adapter
      .init()
      .then(finishLoading)
      .catch((error) => {
        adapter.captureError(error, 'boot');
        finishLoading();
      });
    return () => {
      cancelled = true;
      detach();
    };
  }, [adapter, adGate]);

  useEffect(() => () => audio.dispose(), [audio]);

  const handleGameOver = useCallback(
    (info: GameOverInfo) => {
      adGate.notifyGameplayStop();
      audio.stopMusic();
      setGameOverInfo(info);
      setScreen('gameOver');
      const isPersonalBest = info.score > save.get().pb.score;
      setLastWasPB(isPersonalBest);
      if (isPersonalBest) {
        save.setPersonalBest({
          score: info.score,
          maxCombo: info.maxCombo,
          achievedAt: Date.now(),
        });
        adapter.happyTime(1);
      }
      const goldResult = currency.calculateGoldFromGame(info.score, info.maxCombo, isPersonalBest);
      currency.awardGameGold(goldResult);
      setLastGoldEarned(goldResult);
      currency.endGameSession();
      bonus.endGameSession();
      setBonusActiveOnRetry(false);
    },
    [adGate, adapter, audio, bonus, currency, save],
  );

  const handleHappyMoment = useCallback(
    (intensity: number) => {
      adapter.happyTime(intensity);
    },
    [adapter],
  );

  const handleFirstInput = useCallback(
    (modality: InputModality) => {
      console.info('[App] first input modality', modality);
      audio.resumeOnUserGesture();
    },
    [audio],
  );

  const handleMuteToggle = useCallback(() => {
    const next = !save.get().settings.muted;
    save.patchSettings({ muted: next });
    audio.setMuted(next);
  }, [audio, save]);

  const handleSfx = useCallback(
    (key: SfxKey) => {
      audio.init().catch(() => undefined);
      audio.playSfx(key);
    },
    [audio],
  );

  const handlePauseChange = useCallback(
    (next: boolean) => {
      setPaused(next);
      if (next) {
        audio.stopMusic();
      } else {
        audio.startMusic();
      }
    },
    [audio],
  );

  const handleRendererError = useCallback(
    (error: unknown) => {
      adapter.captureError(error, 'pixi.renderer');
    },
    [adapter],
  );

  useEffect(() => {
    if (screen !== 'playing') return;
    if (openModal === null) return;
    stageControlsRef.current?.pause();
  }, [openModal, screen]);

  const startGame = useCallback(() => {
    audio.init().catch((error) => {
      adapter.captureError(error, 'audio-init');
    });
    audio.resumeOnUserGesture();
    audio.startMusic();
    currency.startGameSession();
    setScreen('playing');
  }, [adapter, audio, currency]);

  const handlePlayBegin = useCallback(() => {
    adGate.notifyGameplayStart();
  }, [adGate]);

  const watchAdForTokens = useCallback(async () => {
    setOpenModal(null);
    let adblock = false;
    try {
      const result = await adGate.showRewarded();
      if (!result.shown && result.reason === 'adblocker') {
        adblock = true;
        return { shown: false, rewarded: false };
      }
      if (!result.shown || !result.rewarded) {
        return { shown: result.shown, rewarded: false };
      }
      const r = bonus.watchAd();
      return { shown: true, rewarded: r.success };
    } finally {
      if (adblock) setAdblockModalOpen(true);
      else setOpenModal('shop');
    }
  }, [adGate, bonus]);

  const watchAdForGold = useCallback(async () => {
    setOpenModal(null);
    let adblock = false;
    try {
      const result = await adGate.showRewarded();
      if (!result.shown && result.reason === 'adblocker') {
        adblock = true;
        return { shown: false, rewarded: false };
      }
      if (!result.shown || !result.rewarded) {
        return { shown: result.shown, rewarded: false };
      }
      const r = currency.watchGoldAd();
      return { shown: true, rewarded: r.success };
    } finally {
      if (adblock) setAdblockModalOpen(true);
      else setOpenModal('shop');
    }
  }, [adGate, currency]);

  const watchAdForRetryBonus = useCallback(async () => {
    const result = await adGate.showRewarded();
    if (result.shown && result.rewarded) {
      setBonusActiveOnRetry(true);
      return { shown: true, rewarded: true };
    }
    if (!result.shown && result.reason === 'adblocker') setAdblockModalOpen(true);
    return { shown: result.shown, rewarded: false };
  }, [adGate]);

  const spendTokenForBonus = useCallback(() => {
    const ok = bonus.activateBonus();
    if (ok) setBonusActiveOnRetry(true);
    return ok;
  }, [bonus]);

  const submitToLeaderboard = useCallback(
    async (username: string): Promise<SubmitOutcome> => {
      const info = gameOverInfo;
      if (!info) {
        return { kind: 'error', message: 'No score to submit.' };
      }
      const player = save.get();
      const submission: ScoreSubmission = {
        playerId: player.playerId,
        username,
        score: info.score,
        playTime: info.playTimeSeconds,
        comboCount: info.maxCombo,
      };
      const { submitScore } = await import('@/leaderboard/leaderboardService');
      const outcome = await submitScore(submission);
      if (outcome.kind === 'submitted' && player.username !== outcome.entry.username) {
        save.setUsername(outcome.entry.username);
      }
      if (outcome.kind === 'error') {
        adapter.captureError(new Error(outcome.message), 'leaderboard.submit');
      }
      return outcome;
    },
    [adapter, gameOverInfo, save],
  );

  const shareScore = useCallback(async () => {
    try {
      const url = await adapter.shareableURL({ score: gameOverInfo?.score ?? 0 });
      if (!url) return { ok: false };
      await navigator.clipboard.writeText(url);
      return { ok: true };
    } catch (error) {
      adapter.captureError(error, 'share-clipboard');
      return { ok: false };
    }
  }, [adapter, gameOverInfo]);

  const retryGame = useCallback(() => {
    setGameOverInfo(null);
    setAttemptId((id) => id + 1);
    setPaused(false);
    audio.startMusic();
    currency.startGameSession();
    setScreen('playing');
  }, [audio, currency]);

  const backToTitle = useCallback(() => {
    setGameOverInfo(null);
    setBonusActiveOnRetry(false);
    bonus.endGameSession();
    setAttemptId((id) => id + 1);
    setPaused(false);
    setScreen('title');
  }, [bonus]);

  const quitGameToTitle = useCallback(() => {
    adGate.notifyGameplayStop();
    audio.stopMusic();
    setBonusActiveOnRetry(false);
    bonus.endGameSession();
    currency.endGameSession();
    setAttemptId((id) => id + 1);
    setPaused(false);
    setScreen('title');
  }, [adGate, audio, bonus, currency]);

  const resumeGame = useCallback(() => {
    stageControlsRef.current?.resume();
  }, []);

  const resetAllProgress = useCallback(() => {
    save.resetAll();
    audio.setMuted(false);
    audio.setMasterVolume(1);
  }, [audio, save]);

  const handleVolumeChange = useCallback(
    (value: number) => {
      save.patchSettings({ masterVolume: value });
      audio.setMasterVolume(value);
    },
    [audio, save],
  );

  const handleMutedToggle = useCallback(
    (muted: boolean) => {
      save.patchSettings({ muted });
      audio.setMuted(muted);
    },
    [audio, save],
  );

  const handleVibrationToggle = useCallback(
    (enabled: boolean) => {
      save.patchSettings({ vibrationEnabled: enabled });
    },
    [save],
  );

  const closeModal = useCallback(() => setOpenModal(null), []);
  const openSettings = useCallback(() => setOpenModal('settings'), []);
  const openHowTo = useCallback(() => setOpenModal('howto'), []);
  const openLeaderboard = useCallback(() => setOpenModal('leaderboard'), []);
  const openShop = useCallback(() => setOpenModal('shop'), []);
  const openPrivacy = useCallback(() => setOpenModal('privacy'), []);
  const openTerms = useCallback(() => setOpenModal('terms'), []);

  const purchaseScoreBoost = useCallback((): { success: boolean; error?: string } => {
    const r = currency.purchaseScoreBoost();
    if (r.success) return { success: true };
    return r.error ? { success: false, error: r.error } : { success: false };
  }, [currency]);

  const purchaseShields = useCallback(
    (bulk: boolean): { success: boolean; error?: string } => {
      const r = currency.purchaseShields(bulk);
      if (r.success) return { success: true };
      return r.error ? { success: false, error: r.error } : { success: false };
    },
    [currency],
  );

  const getScoreBoostMultiplier = useCallback(() => currency.getScoreBoostMultiplier(), [currency]);
  const getSessionBonusMultiplier = useCallback(
    () => (bonusActiveOnRetry ? bonus.getBonusSettings().bonusMultiplier : 1),
    [bonus, bonusActiveOnRetry],
  );
  const getShieldsRemaining = useCallback(() => currency.getShieldsRemaining(), [currency]);
  const consumeShield = useCallback(() => currency.useShield(), [currency]);

  const showGame = screen === 'playing';

  return (
    <div className="app-shell" data-screen={screen}>
      <Suspense fallback={null}>
        <GameStage
          key={attemptId}
          showGame={showGame}
          muted={saveState.settings.muted}
          getScoreBoostMultiplier={getScoreBoostMultiplier}
          getSessionBonusMultiplier={getSessionBonusMultiplier}
          getShieldsRemaining={getShieldsRemaining}
          consumeShield={consumeShield}
          onGameOver={handleGameOver}
          onFirstInput={handleFirstInput}
          onMuteToggle={handleMuteToggle}
          onSfx={handleSfx}
          onPauseChange={handlePauseChange}
          onHappyMoment={handleHappyMoment}
          onPlayBegin={handlePlayBegin}
          onRendererError={handleRendererError}
          controlsRef={stageControlsRef}
        />
      </Suspense>
      {screen === 'title' && (
        <TitleScreen
          ready={ready}
          pokiMode={mode}
          gold={saveState.currency.gold}
          tokens={saveState.bonus.availableTokens}
          lastTokenAdAt={saveState.bonus.lastAdWatchTime}
          lastGoldAdAt={saveState.currency.lastGoldAdWatchTime}
          challengeScore={challengeScore}
          onStart={startGame}
          onOpenLeaderboard={openLeaderboard}
          onOpenSettings={openSettings}
          onOpenHowToPlay={openHowTo}
          onOpenShop={openShop}
          onOpenPrivacy={openPrivacy}
          onOpenTerms={openTerms}
          adsEnabled={ADS_ENABLED}
        />
      )}
      {screen === 'playing' && paused && openModal === null && (
        <PauseOverlay onResume={resumeGame} onQuit={quitGameToTitle} />
      )}
      {screen === 'gameOver' && gameOverInfo && (
        <GameOverScreen
          info={gameOverInfo}
          goldEarned={lastGoldEarned}
          isPersonalBest={lastWasPB}
          previousPersonalBest={saveState.pb.score}
          savedUsername={saveState.username}
          bonusActiveOnRetry={bonusActiveOnRetry}
          availableTokens={saveState.bonus.availableTokens}
          ready={ready}
          leaderboardConfigured={isLeaderboardConfigured()}
          onRetry={retryGame}
          onWatchAdForRetryBonus={watchAdForRetryBonus}
          onSpendTokenForBonus={spendTokenForBonus}
          onSubmitToLeaderboard={submitToLeaderboard}
          onShare={shareScore}
          onBackToTitle={backToTitle}
          adsEnabled={ADS_ENABLED}
        />
      )}
      <SettingsModal
        open={openModal === 'settings'}
        onClose={closeModal}
        settings={saveState.settings}
        reducedMotion={reducedMotion}
        onChangeMasterVolume={handleVolumeChange}
        onToggleMute={handleMutedToggle}
        onToggleVibration={handleVibrationToggle}
        onResetAllProgress={resetAllProgress}
      />
      <HowToPlayModal open={openModal === 'howto'} onClose={closeModal} />
      <PrivacyModal open={openModal === 'privacy'} onClose={closeModal} />
      <TermsModal open={openModal === 'terms'} onClose={closeModal} />
      <AdblockModal open={adblockModalOpen} onClose={() => setAdblockModalOpen(false)} />
      <LeaderboardModal
        open={openModal === 'leaderboard'}
        onClose={closeModal}
        personalBest={saveState.pb.score}
        playerId={saveState.playerId}
      />
      <UpgradeShopModal
        open={openModal === 'shop'}
        onClose={closeModal}
        ready={ready}
        gold={saveState.currency.gold}
        tokens={saveState.bonus.availableTokens}
        scoreBoostLevel={saveState.currency.scoreBoostLevel}
        comboShields={saveState.currency.comboShields}
        lastTokenAdAt={saveState.bonus.lastAdWatchTime}
        lastGoldAdAt={saveState.currency.lastGoldAdWatchTime}
        onPurchaseScoreBoost={purchaseScoreBoost}
        onPurchaseShields={purchaseShields}
        onWatchAdForTokens={watchAdForTokens}
        onWatchAdForGold={watchAdForGold}
        onSfx={handleSfx}
        adsEnabled={ADS_ENABLED}
      />
    </div>
  );
}
